import { supabase } from './supabase';

const IS_DEV = import.meta.env.DEV;

function debugLog(...args: unknown[]): void {
  if (IS_DEV) console.debug('[bootstrap-admin]', ...args);
}

/**
 * Parse the VITE_ADMIN_EMAILS environment variable.
 *
 * Accepts the raw comma-separated string so callers (including tests)
 * can pass any value without relying on import.meta.env.
 *
 * @param raw - raw comma-separated email string; defaults to VITE_ADMIN_EMAILS
 */
export function parseAdminEmails(
  raw: string = import.meta.env.VITE_ADMIN_EMAILS ?? ''
): string[] {
  return raw
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Returns true if `email` is present in `allowlist` (case-insensitive).
 */
export function isAllowlisted(email: string, allowlist: string[]): boolean {
  return allowlist.includes(email.trim().toLowerCase());
}

// Module-level guard: run at most once per page load regardless of how
// many times loadUserProfile is called.
let _bootstrapAttempted = false;

/**
 * Bootstrap the first admin account.
 *
 * Runs at most once per page load. If the current user's email is in
 * VITE_ADMIN_EMAILS and no active admin exists yet, promotes the user
 * to role='admin'.
 *
 * Returns `true` if a promotion was made — the caller should re-fetch
 * the profile so the UI picks up the new role immediately.
 *
 * Idempotent: if an admin already exists or the user is already admin,
 * this is a no-op.
 */
export async function ensureBootstrapAdmin(
  userId: string,
  email: string,
  currentRole: string
): Promise<boolean> {
  if (_bootstrapAttempted) return false;
  _bootstrapAttempted = true;

  const allowlist = parseAdminEmails();

  if (allowlist.length === 0) {
    debugLog('VITE_ADMIN_EMAILS not configured — bootstrap skipped');
    return false;
  }

  if (!isAllowlisted(email, allowlist)) {
    debugLog('Email not in allowlist — bootstrap skipped');
    return false;
  }

  if (currentRole === 'admin') {
    debugLog('User is already admin — bootstrap skipped');
    return false;
  }

  // Confirm no admin exists using a SECURITY DEFINER function so the
  // check works even though non-admins cannot read other users' rows.
  try {
    const { data, error } = await supabase.rpc('has_any_admin');
    if (error) throw error;
    if (Boolean(data)) {
      debugLog('Admin already exists — bootstrap skipped');
      return false;
    }
  } catch (err) {
    // has_any_admin may not be migrated yet — fail safe, do not promote.
    console.warn('[bootstrap-admin] has_any_admin check failed; skipping:', err);
    return false;
  }

  // Promote current user.
  const { error: updateError } = await supabase
    .from('user_profiles')
    .update({ role: 'admin' })
    .eq('id', userId);

  if (updateError) {
    console.error('[bootstrap-admin] Promotion failed:', updateError);
    return false;
  }

  debugLog('Bootstrap promotion applied for', email);
  return true;
}

/**
 * Reset the module-level guard.
 * Exported for tests only — do not call in application code.
 */
export function _resetBootstrapGuard(): void {
  _bootstrapAttempted = false;
}
